/**
 * TrackingPixelsCard.tsx — Admin → Marketing Integrations → Tracking Pixels.
 *
 * The Meta and Google Ads browser pixels, per account (SCRUM-1168).
 *
 * Deliberately its own card, and its own providers, rather than fields on the
 * Ads Connector: that card owns OAuth connections whose credentials are
 * rewritten on every reconnect, and a pixel id pasted in by hand would be wiped
 * by a reconnection nobody would connect to the tracking going quiet. They are
 * also a different kind of value — an ads API credential is a secret, a pixel
 * id is public by construction, since it ships in the page to every visitor.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): the saved ids show read-only (an
 * AdminLockedFieldset) with no Save and no saving hint; nothing says why.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/hooks/useToast';
import {
  upsertMarketingIntegration,
  type MarketingIntegrationRow,
} from '@/services/marketingIntegrations.service';
import { getErrorMessage } from '@/services/apiClient';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';

/** Refused before saving: a wrong id fails SILENTLY once it is live. */
const META_ID = /^[0-9]{10,20}$/;
const ADS_ID = /^AW-[0-9]{6,15}$/;

export function TrackingPixelsCard({ rows }: { rows: MarketingIntegrationRow[] }) {
  const locked = useAdminPageLevel() === 'locked';
  const toast = useToast();
  const queryClient = useQueryClient();

  const meta = rows.find((r) => r.provider === 'meta_pixel');
  const ads = rows.find((r) => r.provider === 'google_ads_pixel');

  const [metaPixelId, setMetaPixelId] = useState('');
  const [conversionId, setConversionId] = useState('');
  const [purchaseLabel, setPurchaseLabel] = useState('');

  // Seeded from what is saved, so the form shows the current state rather than
  // an empty box that looks unconfigured.
  useEffect(() => {
    setMetaPixelId(meta?.publicValues?.pixelId ?? '');
    setConversionId(ads?.publicValues?.conversionId ?? '');
    setPurchaseLabel(ads?.publicValues?.purchaseLabel ?? '');
  }, [meta, ads]);

  const save = useMutation({
    mutationFn: async () => {
      if (metaPixelId.trim()) {
        await upsertMarketingIntegration('meta_pixel', { pixelId: metaPixelId.trim() });
      }
      if (conversionId.trim()) {
        await upsertMarketingIntegration('google_ads_pixel', {
          conversionId: conversionId.trim(),
          purchaseLabel: purchaseLabel.trim() || null,
        });
      }
    },
    onSuccess: () => {
      toast.success('Tracking pixels saved.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'marketing-integrations'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const metaBad = metaPixelId.trim() !== '' && !META_ID.test(metaPixelId.trim());
  const adsBad = conversionId.trim() !== '' && !ADS_ID.test(conversionId.trim());
  const nothingEntered = !metaPixelId.trim() && !conversionId.trim();

  return (
    <Card>
      <CardBody className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Tracking Pixels</h3>
            <p className="text-[11px] text-slate-500">
              Loaded by the patient app to report views, cart activity and purchases back to
              Meta and Google Ads. These ids are public — they are visible in the page source.
            </p>
          </div>
          {(meta?.configured || ads?.configured) && <Badge variant="success">Configured</Badge>}
        </div>

        <AdminLockedFieldset>
        <label className="block space-y-1">
          <span className="text-[11px] font-medium text-slate-600">Meta Pixel ID</span>
          <Input
            value={metaPixelId}
            onChange={(e) => setMetaPixelId(e.target.value)}
            placeholder={locked ? undefined : '565886740600144'}
          />
          {metaBad && (
            <span className="text-[11px] text-red-600">
              Digits only — paste the ID from Events Manager, not the whole snippet.
            </span>
          )}
        </label>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <label className="space-y-1">
            <span className="text-[11px] font-medium text-slate-600">Google Ads conversion ID</span>
            <Input
              value={conversionId}
              onChange={(e) => setConversionId(e.target.value)}
              placeholder={locked ? undefined : 'AW-123456789'}
            />
            {adsBad && (
              <span className="text-[11px] text-red-600">
                Keep the AW- prefix — the tag does not load without it.
              </span>
            )}
          </label>
          <label className="space-y-1">
            <span className="text-[11px] font-medium text-slate-600">Purchase label (optional)</span>
            <Input
              value={purchaseLabel}
              onChange={(e) => setPurchaseLabel(e.target.value)}
              placeholder={locked ? undefined : 'AbC-D_efGhIjKlM'}
            />
          </label>
        </div>
        </AdminLockedFieldset>

        {!locked && (
        <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-3">
          <p className="text-[11px] text-slate-400">
            Saving does not verify the pixel — only the browser can. Confirm receipt in Events
            Manager after the first visit.
          </p>
          <Button
            size="sm"
            onClick={() => save.mutate()}
            disabled={save.isPending || metaBad || adsBad || nothingEntered}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
        )}
      </CardBody>
    </Card>
  );
}
